import React, {useState, useEffect} from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Generate distinct colors for sportsbooks
const generateBookColor = (bookName, index) => {
    const colors = [
        '#3B82F6', // Blue
        '#EF4444', // Red  
        '#10B981', // Green
        '#F59E0B', // Yellow
        '#8B5CF6', // Purple
        '#F97316', // Orange
        '#EC4899', // Pink
        '#06B6D4', // Cyan
        '#84CC16', // Lime
        '#6366F1'  // Indigo
    ];
    return colors[index % colors.length];
};

// Format timestamp for display
const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Transform odds data into Chart.js compatible format
const transformOddsData = (odds, sport, betType, team, homeAway) => {
    if (!odds || !Array.isArray(odds)) return { labels: [], datasets: [] };

    // Get all unique timestamps from all books (chronological order)
    const allTimestamps = new Set();
    odds.forEach(book => {
        if (book.lines && Array.isArray(book.lines)) {
            book.lines.forEach(line => {
                if (line.timestamp) {
                    allTimestamps.add(line.timestamp);
                }
            });
        }
    });

    // Sort timestamps chronologically (oldest to newest)
    const sortedTimestamps = Array.from(allTimestamps).sort();
    const labels = sortedTimestamps.map(formatTimestamp);

    const datasets = odds.map((book, index) => {
        if (!book.lines || !Array.isArray(book.lines)) {
            return {
                label: book.book || `Book ${index + 1}`,
                data: [],
                borderColor: generateBookColor(book.book, index),
                backgroundColor: generateBookColor(book.book, index) + '20',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 5,
                fill: false
            };
        }

        // Create a map of timestamps to values for this book
        const timestampToValue = {};
        book.lines.forEach(line => {
            let value = null;
            
            switch(betType) {
                case 'totals':
                    // Extract runs/pts value for totals
                    if (sport === 'mlb') {
                        value = line.runs;
                    } else {
                        value = line.pts;
                    }
                    break;
                    
                case 'spread':
                case 'runline':
                    // Extract team-specific spread/runline value
                    if (homeAway === 'home' && line.home) {
                        value = sport === 'mlb' ? line.home.runs : line.home.pts;
                    } else if (homeAway === 'away' && line.away) {
                        value = sport === 'mlb' ? line.away.runs : line.away.pts;
                    }
                    break;
                    
                case 'ml':
                    // Extract team-specific moneyline value
                    if (homeAway === 'home') {
                        value = line.home;
                    } else if (homeAway === 'away') {
                        value = line.away;
                    }
                    break;
                    
                default:
                    value = null;
            }

            if (value !== null && value !== undefined) {
                timestampToValue[line.timestamp] = value;
            }
        });

        // Map sorted timestamps to data values (null for missing points)
        const dataPoints = sortedTimestamps.map(timestamp => 
            timestampToValue[timestamp] !== undefined ? timestampToValue[timestamp] : null
        );

        return {
            label: book.book || `Book ${index + 1}`,
            data: dataPoints,
            borderColor: generateBookColor(book.book, index),
            backgroundColor: generateBookColor(book.book, index) + '20',
            tension: 0.1,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            spanGaps: true // Connect line across null values
        };
    });

    return { labels, datasets };
};

// Generate chart title based on bet type and context
const generateChartTitle = (betType, sport, team, homeAway) => {
    switch(betType) {
        case 'totals': 
            return `Total ${sport === 'mlb' ? 'Runs' : 'Points'} Movement`;
        case 'spread': 
            return `${team} Spread Movement`;
        case 'runline': 
            return `${team} Runline Movement`;
        case 'ml': 
            return `${team} Moneyline Movement`;
        default: 
            return 'Line Movement';
    }
};

// Get Y-axis label based on bet type and sport
const getYAxisLabel = (betType, sport) => {
    switch(betType) {
        case 'totals': 
            return sport === 'mlb' ? 'Total Runs' : 'Total Points';
        case 'spread': 
            return 'Spread';
        case 'runline': 
            return 'Runline';
        case 'ml': 
            return 'Moneyline Odds';
        default: 
            return 'Value';
    }
};

// Build dynamic chart configuration
const buildChartConfig = (betType, sport, team, homeAway, isDarkMode) => {
    const textColor = isDarkMode ? '#E5E7EB' : '#374151';
    const gridColor = isDarkMode ? '#374151' : '#E5E7EB';

    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: textColor,
                    font: {
                        size: 10
                    }
                }
            },
            title: {
                display: true,
                text: generateChartTitle(betType, sport, team, homeAway),
                color: textColor,
                font: {
                    size: 16,
                    weight: 'bold'
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        
                        const value = context.parsed.y;
                        if (betType === 'ml') {
                            // Format moneyline odds with + for positive values
                            label += value > 0 ? `+${value}` : value;
                        } else {
                            label += value;
                        }
                        
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    color: gridColor
                },
                ticks: {
                    color: textColor,
                    maxTicksLimit: 8, // Reduce number of x-axis labels
                    font: {
                        size: 10
                    }
                }   
            },
            y: {
                title: {
                    display: true,
                    text: getYAxisLabel(betType, sport),
                    color: textColor
                },
                grid: {
                    color: gridColor
                },
                ticks: {
                    color: textColor,
                    // Set stepSize to 0.5 for spread and totals (exclude runline)
                    stepSize: (betType === 'spread' || betType === 'totals') ? 0.5 : undefined,
                    callback: function(value) {
                        if (['ml', 'spread', 'runline'].includes(betType)) {
                            // Format moneyline ticks with + for positive values
                            return value > 0 ? `+${value}` : value;
                        }
                        return value;
                    }
                }
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };
};

const LineMovementChart = ({ odds, sport, betType, team = null, homeAway = null }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        // Initial theme detection
        const checkTheme = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        
        checkTheme();
        
        // Listen for theme changes
        const observer = new MutationObserver(() => {
            checkTheme();
        });
        
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        
        // Cleanup observer on unmount
        return () => observer.disconnect();
    }, []);

    // Early validation - check for required data
    if (!odds || !Array.isArray(odds) || odds.length === 0) {
        return (
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-4">
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No odds data available
                </div>
            </div>
        );
    }

    // Validate required props for team-specific bet types
    if ((betType === 'spread' || betType === 'runline' || betType === 'ml') && (!team || !homeAway)) {
        return (
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-4">
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Team and homeAway (home/away) are required for {betType} charts
                </div>
            </div>
        );
    }

    // Transform data and build configuration
    const chartData = transformOddsData(odds, sport, betType, team, homeAway);
    const chartConfig = buildChartConfig(betType, sport, team, homeAway, isDarkMode);

    // Check if we have any valid data to display
    const hasValidData = chartData.datasets.some(dataset => dataset.data && dataset.data.length > 0);

    if (!hasValidData) {
        return (
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-4">
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No line movement data available for the selected criteria
                </div>
            </div>
        );
    }

    return (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-2 sm:p-6 mb-6">
            <div className="h-100">
                <Line data={chartData} options={chartConfig} />
            </div>
        </div>
    );
};

export default LineMovementChart;